import React, {useEffect} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {withRouter} from 'react-router-dom';
import Styled from 'styled-components';
import {useIntl} from 'react-intl';
import {FlexColumn, FlexRow} from '@forecast-it/design-system';
import {useForecastFetchQuery} from '../../../shared/hooks/useForecastFetchQuery';
import ProjectHeader from '../shared/ProjectHeader';
import CustomScrollDiv from '../../../shared/components/scroll-bars/custom_scroll_div';
import type {ProjectTranscriptPage_viewer$key} from './__generated__/ProjectTranscriptPage_viewer.graphql';
import type {ProjectTranscriptPage_TranscriptsQuery$data} from './__generated__/ProjectTranscriptPage_TranscriptsQuery.graphql';
import {trackPage, unregisterPageInfo} from '../../../../tracking/amplitude/TrackingV2';

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = Styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
`;

const ContentWrapper = Styled.div`
	padding: 24px 32px;
	max-width: 960px;
`;

const Section = Styled.div`
	margin-bottom: 28px;
`;

const SectionTitle = Styled.div`
	font-size: 13px;
	font-weight: 700;
	color: #6e0fea;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	margin-bottom: 12px;
	padding-bottom: 8px;
	border-bottom: 1px solid #e4dcff;
`;

const MetaGrid = Styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
	gap: 12px 24px;
`;

const MetaItem = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

const MetaLabel = Styled.div`
	font-size: 11px;
	font-weight: 600;
	color: #9a96a8;
	text-transform: uppercase;
	letter-spacing: 0.04em;
`;

const MetaValue = Styled.div`
	font-size: 14px;
	color: #2e2541;
	font-weight: 500;
`;

const OutcomeBadge = Styled.span<{outcome: string}>`
	display: inline-block;
	font-size: 11px;
	font-weight: 700;
	padding: 2px 10px;
	border-radius: 12px;
	letter-spacing: 0.04em;
	background: ${({outcome}) =>
		outcome === 'full' ? '#d1fae5' : outcome === 'roles_only' || outcome === 'deliverables_only' ? '#fef9c3' : '#f3f4f6'};
	color: ${({outcome}) =>
		outcome === 'full' ? '#065f46' : outcome === 'roles_only' || outcome === 'deliverables_only' ? '#92400e' : '#6b7280'};
`;

const TypeBadge = Styled.span`
	display: inline-block;
	font-size: 11px;
	font-weight: 600;
	padding: 2px 10px;
	border-radius: 12px;
	background: #f3eeff;
	color: #6e0fea;
	border: 1px solid #d9c9ff;
`;

const CardList = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const Card = Styled.div`
	background: #fafafa;
	border: 1px solid #ebebeb;
	border-radius: 8px;
	padding: 14px 16px;
`;

const CardTitle = Styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #2e2541;
	margin-bottom: 4px;
`;

const CardDesc = Styled.div`
	font-size: 12px;
	color: #6b6b80;
	line-height: 1.5;
`;

const RoleRow = Styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	background: #fafafa;
	border: 1px solid #ebebeb;
	border-radius: 8px;
	padding: 10px 20px;
`;

const RoleName = Styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #2e2541;
`;

const RoleMeta = Styled.div`
	font-size: 12px;
	color: #9a96a8;
	white-space: nowrap;
`;

const RoleMetaGroup = Styled.div`
	display: flex;
	align-items: center;
	gap: 20px;
`;

const QAItem = Styled.div`
	background: #fafafa;
	border: 1px solid #ebebeb;
	border-radius: 8px;
	padding: 12px 16px;
`;

const QAQuestion = Styled.div`
	font-size: 12px;
	font-weight: 600;
	color: #6b6b80;
	margin-bottom: 4px;
`;

const QAAnswer = Styled.div`
	font-size: 13px;
	color: #2e2541;
	font-weight: 500;
`;

const EmptyState = Styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 80px 32px;
	color: #9a96a8;
	text-align: center;
	gap: 8px;
`;

const EmptyTitle = Styled.div`font-size: 16px; font-weight: 600; color: #6b6b80;`;
const EmptyDesc = Styled.div`font-size: 13px;`;

const SectionNote = Styled.div`
	font-size: 11px;
	color: #9a96a8;
	margin-bottom: 10px;
	font-style: italic;
`;

// ─── Transcript content types ─────────────────────────────────────────────────

interface TranscriptData {
	sow_filename?: string;
	sow_type?: string;
	extraction_outcome?: string;
	extraction_metadata?: {
		project_name?: string;
		client_name?: string;
		start_date?: string;
		end_date?: string;
		total_fee?: number;
		budget_type?: string;
	};
	extracted_deliverables?: Array<{
		name: string;
		description?: string;
		start_date?: string | null;
		end_date?: string | null;
		total_hours?: number | null;
	}>;
	extracted_roles?: Array<{
		name: string;
		hours?: number | null;
		rate?: number | null;
	}>;
	nova_filled_fields?: string[];
	reconciliation_questions?: Array<{
		id?: string;
		question_type?: string;
		question: string;
		options?: string[];
		field_ref?: string;
		best_guess?: string | null;
		answer?: string | null;
	}>;
}

const OUTCOME_LABEL_IDS: Record<string, string> = {
	full: 'project.transcript_outcome_full',
	roles_only: 'project.transcript_outcome_roles_only',
	deliverables_only: 'project.transcript_outcome_deliverables_only',
	metadata_only: 'project.transcript_outcome_metadata_only',
};

const transcriptsQuery = graphql`
	query ProjectTranscriptPage_TranscriptsQuery($projectId: ID!) {
		viewer {
			projectTranscripts(projectId: $projectId) {
				id
				transcript
				createdAt
			}
		}
	}
`;

// ─── Page component ───────────────────────────────────────────────────────────

interface ViewerShape {
	company?: {
		currency?: string | null;
	} | null;
	project: {
		id: string;
		name: string;
		companyProjectId: number;
		client?: {
			id: string;
			name: string | null;
		} | null;
	} | null;
}

interface Props {
	viewer: ViewerShape & ProjectTranscriptPage_viewer$key;
}

const ProjectTranscriptPage: React.FC<Props> = ({viewer}) => {
	const {formatMessage} = useIntl();
	const companyCurrency = viewer.company?.currency ?? 'USD';

	useEffect(() => {
		document.title = `${formatMessage({id: 'project.transcript_page_title'})} - Forecast`;
		const info = trackPage('Project Transcript');
		return () => {
			if (typeof info === 'string') unregisterPageInfo(info);
		};
	}, [formatMessage]);

	const project = viewer?.project;

	const {data, fetch, loading} = useForecastFetchQuery<ProjectTranscriptPage_TranscriptsQuery$data, {projectId: string}>(
		transcriptsQuery
	);

	useEffect(() => {
		if (project?.id) {
			fetch({projectId: project.id});
		}
	}, [project?.id]);

	const transcripts = (data?.viewer?.projectTranscripts ?? []) as Array<{
		id: string;
		transcript: string | null;
		createdAt: string | null;
	}>;
	const latest = transcripts.length > 0 ? transcripts[transcripts.length - 1] : null;

	let parsed: TranscriptData | null = null;
	if (latest?.transcript) {
		try {
			parsed = JSON.parse(latest.transcript) as TranscriptData;
		} catch {
			// leave null — show raw fallback
		}
	}

	const meta = parsed?.extraction_metadata;
	const deliverables = parsed?.extracted_deliverables ?? [];
	const roles = parsed?.extracted_roles ?? [];
	const questions = (parsed?.reconciliation_questions ?? []).filter(q => q.answer && q.answer !== 'Skip');

	return (
		<PageWrapper>
			<ProjectHeader
				title={formatMessage({id: 'project.transcript_page_title'})}
				project={viewer.project as never}
				psProject={null}
				projectGroup={null}
			/>
			<CustomScrollDiv>
				<ContentWrapper>
					{loading && (
						<EmptyState>
							<EmptyDesc>{formatMessage({id: 'project.transcript_loading'})}</EmptyDesc>
						</EmptyState>
					)}

					{!loading && !parsed && !latest && (
						<EmptyState>
							<EmptyTitle>{formatMessage({id: 'project.transcript_empty_title'})}</EmptyTitle>
							<EmptyDesc>{formatMessage({id: 'project.transcript_empty_desc'})}</EmptyDesc>
						</EmptyState>
					)}

					{!loading && latest && !parsed && (
						<EmptyState>
							<EmptyTitle>{formatMessage({id: 'project.transcript_error_title'})}</EmptyTitle>
							<EmptyDesc>{formatMessage({id: 'project.transcript_error_desc'})}</EmptyDesc>
						</EmptyState>
					)}

					{!loading && parsed && (
						<FlexColumn gap="none">
							{/* Overview */}
							<Section>
								<SectionTitle>{formatMessage({id: 'project.transcript_section_overview'})}</SectionTitle>
								<FlexRow gap="s" alignItems="center" style={{flexWrap: 'wrap'}}>
									{parsed.sow_filename && <MetaValue>{parsed.sow_filename}</MetaValue>}
									{parsed.sow_type && (
										<TypeBadge>
											{parsed.sow_type === 'FIXED_PRICE'
												? formatMessage({id: 'project.transcript_sow_type_fixed_price'})
												: parsed.sow_type === 'TIME_AND_MATERIALS'
												? formatMessage({id: 'project.transcript_sow_type_tnm'})
												: parsed.sow_type}
										</TypeBadge>
									)}
									{parsed.extraction_outcome && (
										<OutcomeBadge outcome={parsed.extraction_outcome}>
											{OUTCOME_LABEL_IDS[parsed.extraction_outcome]
												? formatMessage({id: OUTCOME_LABEL_IDS[parsed.extraction_outcome]})
												: parsed.extraction_outcome}
										</OutcomeBadge>
									)}
								</FlexRow>
							</Section>

							{/* Project Details */}
							{meta && (
								<Section>
									<SectionTitle>{formatMessage({id: 'project.transcript_section_details'})}</SectionTitle>
									<MetaGrid>
										{project?.client?.name && (
											<MetaItem>
												<MetaLabel>{formatMessage({id: 'project.transcript_label_client'})}</MetaLabel>
												<MetaValue>{project.client.name}</MetaValue>
											</MetaItem>
										)}
										{meta.start_date && (
											<MetaItem>
												<MetaLabel>
													{formatMessage({id: 'project.transcript_label_start_date'})}
												</MetaLabel>
												<MetaValue>{meta.start_date}</MetaValue>
											</MetaItem>
										)}
										{meta.end_date && (
											<MetaItem>
												<MetaLabel>
													{formatMessage({id: 'project.transcript_label_end_date'})}
												</MetaLabel>
												<MetaValue>{meta.end_date}</MetaValue>
											</MetaItem>
										)}
										{meta.total_fee != null && (
											<MetaItem>
												<MetaLabel>
													{formatMessage({id: 'project.transcript_label_total_fee'})}
												</MetaLabel>
												<MetaValue>
													{meta.total_fee.toLocaleString(undefined, {
														style: 'currency',
														currency: companyCurrency,
														maximumFractionDigits: 0,
													})}
												</MetaValue>
											</MetaItem>
										)}
									</MetaGrid>
								</Section>
							)}

							{/* Deliverables */}
							{deliverables.length > 0 && (
								<Section>
									<SectionTitle>
										{formatMessage(
											{id: 'project.transcript_section_deliverables'},
											{count: deliverables.length}
										)}
									</SectionTitle>
									<CardList>
										{deliverables.map((d, i) => (
											<Card key={i}>
												<CardTitle>{d.name}</CardTitle>
												{d.description && <CardDesc>{d.description}</CardDesc>}
												{d.total_hours != null && (
													<RoleMeta style={{marginTop: 6}}>
														{formatMessage(
															{id: 'project.transcript_hours_estimated'},
															{hours: d.total_hours}
														)}
													</RoleMeta>
												)}
											</Card>
										))}
									</CardList>
								</Section>
							)}

							{/* Roles */}
							{roles.length > 0 && (
								<Section>
									<SectionTitle>
										{formatMessage({id: 'project.transcript_section_roles'}, {count: roles.length})}
									</SectionTitle>
									<SectionNote>{formatMessage({id: 'project.transcript_roles_note'})}</SectionNote>
									<CardList>
										{roles.map((r, i) => (
											<RoleRow key={i}>
												<RoleName>{r.name}</RoleName>
												<RoleMetaGroup>
													{r.hours != null && (
														<RoleMeta>
															{formatMessage(
																{id: 'project.transcript_hours_estimated'},
																{hours: r.hours}
															)}
														</RoleMeta>
													)}
													{r.hours != null && r.rate != null && (
														<RoleMeta style={{color: '#d1d1e0'}}>|</RoleMeta>
													)}
													{r.rate != null && (
														<RoleMeta>
															{formatMessage(
																{id: 'project.transcript_rate_per_hour'},
																{
																	rate: r.rate.toLocaleString(undefined, {
																		style: 'currency',
																		currency: companyCurrency,
																		maximumFractionDigits: 0,
																	}),
																}
															)}
														</RoleMeta>
													)}
												</RoleMetaGroup>
											</RoleRow>
										))}
									</CardList>
								</Section>
							)}

							{/* Reconciliation Q&A */}
							{questions.length > 0 && (
								<Section>
									<SectionTitle>
										{formatMessage({id: 'project.transcript_section_qa'}, {count: questions.length})}
									</SectionTitle>
									<CardList>
										{questions.map((q, i) => (
											<QAItem key={i}>
												<QAQuestion>{q.question}</QAQuestion>
												<QAAnswer>{q.answer}</QAAnswer>
											</QAItem>
										))}
									</CardList>
								</Section>
							)}
						</FlexColumn>
					)}
				</ContentWrapper>
			</CustomScrollDiv>
		</PageWrapper>
	);
};

export const ProjectTranscriptPageQuery = graphql`
	query ProjectTranscriptPage_Query($projectId: String) {
		viewer {
			actualPersonId
			component(name: "project_transcript")
			project(id: $projectId) {
				id
				name
				companyProjectId
				...ProjectHeader_project
				...SecondaryNavigation_project
			}
			...ProjectTranscriptPage_viewer @arguments(projectId: $projectId)
		}
	}
`;
// NOTE: client { id, name } is fetched via the fragment below and surfaced through ViewerShape

export default withRouter(
	createFragmentContainer(ProjectTranscriptPage as React.ComponentType<never>, {
		viewer: graphql`
			fragment ProjectTranscriptPage_viewer on Viewer @argumentDefinitions(projectId: {type: "String"}) {
				actualPersonId
				company {
					currency
				}
				project(id: $projectId) {
					id
					name
					companyProjectId
					client {
						id
						name
					}
					...ProjectHeader_project
					...SecondaryNavigation_project
				}
			}
		`,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	}) as unknown as React.ComponentType<any>
);
